// Dashboard.js
//
// Conectado aos dados reais de escolas/{idEscola}/dashboard/geral (ver
// dashboardData.js e backend/dashboard.js).

import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, AlertTriangle, BookOpen, GraduationCap, Cake } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

import './dashboard.css';
import { fetchDashboardData, fetchHistoricoCrescimento } from './dashboardData';
import GraficoCrescimento from './GraficoCrescimento';

const CORES_GENERO = { M: '#3b82f6', F: '#ec4899', naoInformado: '#94a3b8' };
const ROTULO_GENERO = { M: 'Masculino', F: 'Feminino', naoInformado: 'Não informado' };

const NOME_MES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export default function Dashboard({ escolaId }) {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const [dadosEstatisticos, setDadosEstatisticos] = useState({});
  const [alunosComFalta, setAlunosComFalta] = useState([]);
  const [aproveitamentoPorTurma, setAproveitamentoPorTurma] = useState([]);
  const [porGenero, setPorGenero] = useState({ M: 0, F: 0, naoInformado: 0 });
  const [porFaixaEtaria, setPorFaixaEtaria] = useState([]);
  const [aniversariantesDoMes, setAniversariantesDoMes] = useState([]);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [historicoCrescimento, setHistoricoCrescimento] = useState([]);

  useEffect(() => {
    let cancelado = false;

    const carregarDashboard = async () => {
      try {
        setLoading(true);
        setErro(null);
        const [resposta, historico] = await Promise.all([
          fetchDashboardData(escolaId),
          fetchHistoricoCrescimento(escolaId),
        ]);
        if (cancelado) return;

        setDadosEstatisticos(resposta.dadosEstatisticos);
        setAlunosComFalta(resposta.alunosComFalta);
        setAproveitamentoPorTurma(resposta.aproveitamentoPorTurma);
        setPorGenero(resposta.porGenero);
        setPorFaixaEtaria(resposta.porFaixaEtaria);
        setAniversariantesDoMes(resposta.aniversariantesDoMes);
        setAtualizadoEm(resposta.atualizadoEm);
        setHistoricoCrescimento(historico);
      } catch (error) {
        console.error("Falha ao montar o dashboard:", error);
        if (!cancelado) setErro('Não foi possível carregar os dados do dashboard.');
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    if (escolaId) carregarDashboard();

    return () => { cancelado = true; };
  }, [escolaId]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>A carregar dados do servidor...</div>;
  }

  if (erro) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#b91c1c' }}>{erro}</div>;
  }

  const dadosGenero = Object.entries(porGenero)
    .filter(([, qtd]) => qtd > 0)
    .map(([chave, qtd]) => ({ nome: ROTULO_GENERO[chave] || chave, qtd, cor: CORES_GENERO[chave] || '#94a3b8' }));

  const dadosFaixaEtaria = porFaixaEtaria.filter((f) => f.qtd > 0);

  const mesAtualNome = NOME_MES[new Date().getMonth()];

  return (
    <div className="dash-container">

      <div className="dash-header">
        <h1 className="dash-title">Visão Geral</h1>
        <p className="dash-subtitle">Acompanhe as estatísticas e o crescimento da sua Escola Dominical.</p>
      </div>

      {/* SECÇÃO DE ALERTA: FALTAS RECORRENTES (3+ faltas seguidas, ver
          backend/dashboard.js) */}
      {alunosComFalta?.length > 0 && (
        <div className="alert-box">
          <div className="alert-header">
            <AlertTriangle size={20} />
            Atenção: Alunos com faltas seguidas
          </div>
          <div className="alert-list">
            {alunosComFalta.map(aluno => (
              <div key={aluno.id} className="alert-item">
                <div className="alert-item-info">
                  <h4>{aluno.nome}</h4>
                  <p>Turma: {aluno.turmaNome}</p>
                </div>
                <div className="alert-badge">{aluno.faltasSeguidas} faltas</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECÇÃO DE KPIs (Indicadores Chave) */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-info">
            <h3>Alunos Matriculados</h3>
            <p className="kpi-value">{dadosEstatisticos.numAlunos}</p>
          </div>
          <div className="kpi-icon" style={{ color: '#3b82f6' }}><Users size={28} /></div>
        </div>

        <div className="kpi-card">
          <div className="kpi-info">
            <h3>Professores</h3>
            <p className="kpi-value">{dadosEstatisticos.numProfessores}</p>
          </div>
          <div className="kpi-icon" style={{ color: '#f59e0b' }}><GraduationCap size={28} /></div>
        </div>

        <div className="kpi-card">
          <div className="kpi-info">
            <h3>Turmas</h3>
            <p className="kpi-value">{dadosEstatisticos.numTurmas}</p>
          </div>
          <div className="kpi-icon" style={{ color: '#10b981' }}><BookOpen size={28} /></div>
        </div>

        <div className="kpi-card">
          <div className="kpi-info">
            <h3>Aproveitamento Médio (mês)</h3>
            <p className="kpi-value">{dadosEstatisticos.aproveitamentoMedioMensal}%</p>
          </div>
          <div className="kpi-icon" style={{ color: '#8b5cf6' }}><TrendingUp size={28} /></div>
        </div>
      </div>

      {/* SECÇÃO DE GRÁFICOS: aproveitamento por turma, gênero e faixa
          etária dos alunos (todos dados reais, vindos de dashboard/geral) */}
      <div className="charts-grid">

        <GraficoCrescimento historico={historicoCrescimento} />

        {aproveitamentoPorTurma.length > 0 && (
          <div className="chart-card">
            <h2 className="chart-title"><TrendingUp size={18} color="#8b5cf6" /> Aproveitamento por turma (mês atual)</h2>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={aproveitamentoPorTurma} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="turma" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} cursor={{ fill: '#f1f5f9' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="aproveitamento" name="Aproveitamento (%)" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {dadosGenero.length > 0 && (
          <div className="chart-card">
            <h2 className="chart-title"><Users size={18} color="#3b82f6" /> Alunos por gênero</h2>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={dadosGenero} dataKey="qtd" nameKey="nome" cx="50%" cy="50%" outerRadius={90} label>
                    {dadosGenero.map((entrada, i) => (
                      <Cell key={i} fill={entrada.cor} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {dadosFaixaEtaria.length > 0 && (
          <div className="chart-card">
            <h2 className="chart-title"><Users size={18} color="#10b981" /> Alunos por faixa etária</h2>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={dadosFaixaEtaria} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="faixa" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} cursor={{ fill: '#f1f5f9' }} />
                  <Bar dataKey="qtd" name="Alunos" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* LISTA DE ANIVERSARIANTES DO MÊS (alunos + professores) */}
        <div className="chart-card">
          <h2 className="chart-title"><Cake size={18} color="#f59e0b" /> Aniversariantes de {mesAtualNome}</h2>
          {aniversariantesDoMes.length === 0 && <p style={{ fontSize: 14, color: '#64748b' }}>Ninguém faz aniversário este mês.</p>}
          {aniversariantesDoMes.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {aniversariantesDoMes.map((pessoa) => (
                <li key={`${pessoa.tipo}-${pessoa.id}`} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 14,
                }}>
                  <span>
                    <strong>Dia {pessoa.dia}</strong> — {pessoa.nome}
                    {pessoa.turmaNome ? ` (${pessoa.turmaNome})` : ''}
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase' }}>
                    {pessoa.tipo === 'professor' ? 'Professor(a)' : 'Aluno(a)'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {atualizadoEm && (
        <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
          Atualizado em {atualizadoEm.toDate ? atualizadoEm.toDate().toLocaleString('pt-BR') : ''}
        </p>
      )}
    </div>
  );
}